import React, { useEffect } from "react";
import { connect } from "react-redux";

import { FormattedMessage } from "react-intl";
import { Redirect } from "react-router-dom";

import { LoadingSpinner } from "@mapmycustomers/ui";

import { isSignedIn } from "@app/store/auth";
import { loggedIn, login, logout } from "@app/store/auth/actions";
import { getError, getMe } from "@app/store/iam";
import { isManager, isMember, isOwner, isReadOnly } from "@app/store/iam/util";
import { RootState } from "@app/store/rootReducer";

import Path from "../../enum/Path";
import authService from "../../store/auth/AuthService";
import hasTrialExpired from "../../util/hasTrialExpired";

import styles from "./RootPage.module.scss";

export const RootPage = ({
  error,
  isSignedIn,
  loggedIn,
  login,
  logout,
  me,
}: ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const hasToken = authService.hasToken();

  useEffect(() => {
    // if token is not in the store and we're not in the loading state
    if (isSignedIn === false) {
      // if token is in the AuthService...
      if (hasToken) {
        // ...proceed with it
        loggedIn({ token: authService.getToken() });
      } else {
        // otherwise, go log in
        login();
      }
    }

    // if token is in the store
    if (isSignedIn === true) {
      // ...and we've got a profile data
      if (me) {
        // logout if it's some weird unknown role
        if (!isOwner(me) && !isManager(me) && !isMember(me) && !isReadOnly(me)) {
          logout();
          return;
        }
      } else {
        // if we're logged in but don't have a profile yet...
        loggedIn({});
      }
    }
  }, [hasToken, isSignedIn, loggedIn, login, logout, me]);

  // if token is not in the store and we're not in the loading state
  if (isSignedIn === false) {
    return null;
  }

  // if token is in the store
  if (isSignedIn === true) {
    // ...and we've got a profile data
    if (me) {
      // render nothing if it's some weird unknown role
      // user will be log out via code in useEffect
      if (!isOwner(me) && !isManager(me) && !isMember(me) && !isReadOnly(me)) {
        return null;
      }

      if (hasTrialExpired(me.organization)) {
        return <Redirect to={Path.BILLING} />;
      }

      // otherwise, redirect to the home page
      return <Redirect to={Path.DASHBOARD} />;
    }

    return (
      <LoadingSpinner global>
        <FormattedMessage
          id="auth.in_progress"
          defaultMessage="Logging in, please wait..."
          description="Message visible when user authentication is in progress"
        />
      </LoadingSpinner>
    );
  }

  // if there was a critical error (fetching the profile), show a message
  if (error) {
    return (
      <div className={styles.container}>
        <FormattedMessage
          id="auth.server_error"
          defaultMessage="
            Server error occurred. Please retry in several minutes.<br />
            If the problem persists, please contact our support team.
          "
          description="Authentication failed due to a server issue."
        />
      </div>
    );
  }

  // otherwise, we're loading user's profile. Show the "please stand by"-like message.
  return (
    <LoadingSpinner global>
      <FormattedMessage
        id="auth.in_progress"
        defaultMessage="Logging in, please wait..."
        description="Message visible when user authentication is in progress"
      />
    </LoadingSpinner>
  );
};

const mapStateToProps = (state: RootState) => ({
  error: getError(state),
  isSignedIn: isSignedIn(state),
  me: getMe(state),
});

const mapDispatchToProps = {
  loggedIn,
  login,
  logout,
};

export default connect(mapStateToProps, mapDispatchToProps)(RootPage);
